/**
 * Centralized API endpoint configuration
 *
 * Benefits:
 * - Single source of truth for all API routes
 * - Easy to update API versions (v1, v2, v3, etc.)
 * - Type-safe with IntelliSense support
 * - Self-documenting API structure
 */

export const API_ENDPOINTS = {
  /**
   * Submissions endpoints (v1)
   */
  submissions: {
    list: "submissions",
    detail: (id: number) => `submissions/${id}`,
    archive: (id: number) => `submissions/${id}`,
    classCodesSearch: "submissions/class-codes/search",
    batchAssign: "submissions/batch/assign",
    underwriters: "submissions/underwriters",
  },

  /**
   * General Liability endpoints (v1)
   */
  generalLiability: {
    coverage: (submissionId: number) =>
      `submissions/${submissionId}/general-liability`,
    scheduleOfHazard: (submissionId: number) =>
      `submissions/${submissionId}/general-liability/schedule-of-hazard`,
  },

  /**
   * Premises endpoints (v1)
   */
  premises: {
    list: (submissionId: number) => `submissions/${submissionId}/premises`,
    detail: (submissionId: number, premiseId: number) =>
      `submissions/${submissionId}/premises/${premiseId}`,
  },

  /**
   * Building endpoints (v1)
   */
  buildings: {
    list: (submissionId: number, premiseId: number) =>
      `submissions/${submissionId}/premises/${premiseId}/buildings`,
    detail: (submissionId: number, buildingId: number) =>
      `submissions/${submissionId}/buildings/${buildingId}`,
  },

  /**
   * Class Codes endpoints
   * Note: Uses v2 API
   */
  classCodes: {
    all: "api/v2/class-codes/gl",
    property: "api/v2/class-codes/property",
    list: "api/v2/class-codes",
    detail: (code: string) => `api/v2/class-codes/${code}`,
  },

  /**
   * Navigator endpoints (v1)
   */
  marketFinder: {
    list: "market-finder/",
    detail: (id: number) => `market-finder/${id}`,
    archive: (id: number) => `market-finder/${id}`,
    underwriters: "market-finder/underwriters",
    generalLiability: (appId: number) =>
      `market-finder/${appId}/general-liability`,
    premises: {
      list: (appId: number) => `market-finder/${appId}/premises`,
      detail: (appId: number, premiseId: number) =>
        `market-finder/${appId}/premises/${premiseId}`,
    },
    buildings: {
      list: (appId: number, premiseId: number) =>
        `market-finder/${appId}/premises/${premiseId}/buildings`,
      detail: (appId: number, buildingId: number) =>
        `market-finder/${appId}/buildings/${buildingId}`,
    },
    lossRun: (appId: number) => `market-finder/${appId}/loss-run`,
    lossPeriod: (appId: number, periodId: number) =>
      `market-finder/${appId}/loss-periods/${periodId}`,
    lossClaim: (appId: number, claimId: number) =>
      `market-finder/${appId}/loss-claims/${claimId}`,
    coverageCatalog: "market-finder/coverage-catalog",
    requestedCoverages: (appId: number) =>
      `market-finder/${appId}/requested-coverages`,
    coverageData: (appId: number) => `market-finder/${appId}/coverage-data`,
    coverageDataFamily: (appId: number, family: string, rowId?: number) =>
      `market-finder/${appId}/coverage-data/families/${family}${rowId ? `/${rowId}` : ""}`,
    coverageDataItem: (appId: number, itemType: string, rowId: number) =>
      `market-finder/${appId}/coverage-data/items/${itemType}/${rowId}`,
    coverLetters: (appId: number) => `market-finder/${appId}/cover-letters`,
    coverLetterGenerate: (appId: number) =>
      `market-finder/${appId}/cover-letters/generate`,
    coverLetterDetail: (appId: number, clId: number) =>
      `market-finder/${appId}/cover-letters/${clId}`,
    aiAssist: "market-finder/ai-assist",
    userPreferences: "market-finder/user-preferences",
    hitRates: (classCodes: string[]) =>
      `market-finder/hit-rates?class_codes=${classCodes.join(",")}`,
    marketSuggestions: (appId: number) =>
      `market-finder/${appId}/market-suggestions`,
    refreshMarketSuggestions: (appId: number) =>
      `market-finder/${appId}/market-suggestions:refresh`,
    packets: (appId: number) => `market-finder/${appId}/packets`,
    packetDetail: (appId: number, packetId: number) =>
      `market-finder/${appId}/packets/${packetId}`,
    packetAttachments: (appId: number, packetId: number) =>
      `market-finder/${appId}/packets/${packetId}/attachments`,
    packetRecipients: (appId: number, packetId: number) =>
      `market-finder/${appId}/packets/${packetId}/recipients`,
    packetCoverLetterGenerate: (appId: number, packetId: number) =>
      `market-finder/${appId}/packets/${packetId}/cover-letter/generate`,
    packetCoverLetter: (appId: number, packetId: number) =>
      `market-finder/${appId}/packets/${packetId}/cover-letter`,
    packetCoverLetterNew: (appId: number, packetId: number) =>
      `market-finder/${appId}/packets/${packetId}/cover-letter/new`,
    packetCoverLetterRevisions: (appId: number, packetId: number) =>
      `market-finder/${appId}/packets/${packetId}/cover-letter/revisions`,
    packetPreview: (appId: number, packetId: number) =>
      `market-finder/${appId}/packets/${packetId}/preview`,
    packetSend: (appId: number, packetId: number) =>
      `market-finder/${appId}/packets/${packetId}/send`,
    agentConversations: (appId: number) =>
      `market-finder/${appId}/agent-conversations`,
    agentConversationAttachments: (appId: number) =>
      `market-finder/${appId}/agent-conversations/attachments`,
    conversations: (appId: number) => `market-finder/${appId}/conversations`,
    agentOriginThreadHydrate: (appId: number) =>
      `market-finder/${appId}/agent-origin-thread/hydrate`,
    conversationMessages: (appId: number, conversationId: number) =>
      `market-finder/${appId}/conversations/${conversationId}/messages`,
    conversationMessageEmail: (appId: number, messageId: number) =>
      `market-finder/${appId}/conversations/messages/${messageId}/email`,
    applicationReviewGroups: (appId: number) =>
      `market-finder/${appId}/conversations/review-groups`,
    // Application-scoped reject: this submission's candidates leave the
    // snapshot so the card disappears here, but the group stays in the
    // admin review queue. Distinct from group dismiss.
    applicationReviewGroupReject: (appId: number, groupId: number) =>
      `market-finder/${appId}/conversations/review-groups/${groupId}/reject`,
    reviewGroups: "market-finder/review-groups",
    reviewGroup: (groupId: number) => `market-finder/review-groups/${groupId}`,
    reviewGroupResolve: (groupId: number) =>
      `market-finder/review-groups/${groupId}/resolve`,
    reviewGroupDismiss: (groupId: number) =>
      `market-finder/review-groups/${groupId}/dismiss`,
    reviewGroupArchive: (groupId: number) =>
      `market-finder/review-groups/${groupId}/archive`,
    reviewGroupUnarchive: (groupId: number) =>
      `market-finder/review-groups/${groupId}/unarchive`,
    reviewGroupMemberEmail: (messageId: number) =>
      `market-finder/review-groups/messages/${messageId}/email`,
    conversationEmailLinkBlastRadius: (appId: number, messageId: number) =>
      `market-finder/${appId}/conversations/messages/${messageId}/link-blast-radius`,
    conversationMessageRead: (appId: number, messageId: number) =>
      `market-finder/${appId}/conversations/messages/${messageId}/read`,
    conversationMessageInterpretationRetry: (appId: number, messageId: number) =>
      `market-finder/${appId}/conversations/messages/${messageId}/interpretation/retry`,
    conversationEmailDetach: (appId: number, messageId: number) =>
      `market-finder/${appId}/conversations/emails/${messageId}/detach`,
    conversationEmailAttach: (appId: number, messageId: number) =>
      `market-finder/${appId}/conversations/emails/${messageId}/attach`,
    conversationInterpretation: (appId: number, conversationId: number) =>
      `market-finder/${appId}/conversations/${conversationId}/interpretation`,
    conversationFact: (appId: number, factId: number) =>
      `market-finder/${appId}/conversations/facts/${factId}`,
    conversationFactDismiss: (appId: number, factId: number) =>
      `market-finder/${appId}/conversations/facts/${factId}/dismiss`,
    questions: (appId: number, conversationId?: number) =>
      conversationId != null
        ? `market-finder/${appId}/questions?conversation_id=${conversationId}`
        : `market-finder/${appId}/questions`,
    accountAnswers: (appId: number) => `market-finder/${appId}/account-answers`,
    accountAnswer: (appId: number, answerId: number) =>
      `market-finder/${appId}/account-answers/${answerId}`,
    accountAnswerLinkConfirm: (appId: number, linkId: number) =>
      `market-finder/${appId}/account-answer-links/${linkId}/confirm`,
    accountAnswerLinkDismiss: (appId: number, linkId: number) =>
      `market-finder/${appId}/account-answer-links/${linkId}/dismiss`,
    question: (appId: number, questionId: number) =>
      `market-finder/${appId}/questions/${questionId}`,
    questionReopen: (appId: number, questionId: number) =>
      `market-finder/${appId}/questions/${questionId}/reopen`,
    questionClose: (appId: number, questionId: number) =>
      `market-finder/${appId}/questions/${questionId}/close`,
    questionLinks: (appId: number, questionId: number) =>
      `market-finder/${appId}/questions/${questionId}/links`,
    questionLinkConfirm: (appId: number, linkId: number) =>
      `market-finder/${appId}/question-links/${linkId}/confirm`,
    questionLinkDismiss: (appId: number, linkId: number) =>
      `market-finder/${appId}/question-links/${linkId}/dismiss`,
    proposedActions: (appId: number, conversationId?: number, messageId?: number) => {
      const params = new URLSearchParams();
      if (conversationId != null) params.set("conversation_id", String(conversationId));
      if (messageId != null) params.set("message_id", String(messageId));
      const query = params.toString();
      return `market-finder/${appId}/proposed-actions${query ? `?${query}` : ""}`;
    },
    proposedActionApprove: (appId: number, actionId: number) =>
      `market-finder/${appId}/proposed-actions/${actionId}/approve`,
    proposedActionApproveBatch: (appId: number) =>
      `market-finder/${appId}/proposed-actions/approve-batch`,
    proposedActionDismiss: (appId: number, actionId: number) =>
      `market-finder/${appId}/proposed-actions/${actionId}/dismiss`,
    proposedActionSnooze: (appId: number, actionId: number) =>
      `market-finder/${appId}/proposed-actions/${actionId}/snooze`,
    actionDraft: (appId: number, draftId: number) =>
      `market-finder/${appId}/action-drafts/${draftId}`,
    groupDraft: (appId: number, draftId: number) =>
      `market-finder/${appId}/group-drafts/${draftId}`,
    relays: (appId: number, conversationId?: number) =>
      conversationId != null
        ? `market-finder/${appId}/relays?conversation_id=${conversationId}`
        : `market-finder/${appId}/relays`,
    conversationReply: (appId: number, conversationId: number) =>
      `market-finder/${appId}/conversations/${conversationId}/reply`,
    conversationReplyDefaults: (
      appId: number,
      conversationId: number,
      targetMessageId?: number,
      replyAll = true,
    ) => {
      const params = new URLSearchParams({ reply_all: String(replyAll) });
      if (targetMessageId != null) {
        params.set("target_message_id", String(targetMessageId));
      }
      return `market-finder/${appId}/conversations/${conversationId}/reply-defaults?${params}`;
    },
    conversationReplyAttachments: (appId: number, conversationId: number) =>
      `market-finder/${appId}/conversations/${conversationId}/reply/attachments`,
    emailMailTips: (appId: number) =>
      `market-finder/${appId}/email-preflight/mail-tips`,
    communicationsSummary: "market-finder/communications/summary",
    demoConfig: "market-finder/demo/config",
    demoAgentMessage: (conversationId: number) =>
      `market-finder/demo/conversations/${conversationId}/agent-message`,
    demoAgentThread: (packetId: number) =>
      `market-finder/demo/packets/${packetId}/agent-thread`,
    demoReset: "market-finder/demo/reset",
    markets: (lob?: string, includeInactive?: boolean) => {
      const qs = new URLSearchParams();
      if (lob) qs.set("lob", lob);
      if (includeInactive) qs.set("include_inactive", "true");
      const query = qs.toString();
      return query ? `market-finder/markets?${query}` : "market-finder/markets";
    },
    marketCreate: "market-finder/markets",
    marketUpdate: (carrierId: string) => `market-finder/markets/${carrierId}`,
    marketImage: (carrierId: string) =>
      `market-finder/markets/${carrierId}/image`,
    marketContactsDirectory: "market-finder/contacts",
    marketContacts: (carrierId: string) =>
      `market-finder/markets/${carrierId}/contacts`,
    marketContact: (contactId: number) =>
      `market-finder/contacts/${contactId}`,
    marketContactArchive: (contactId: number, archive: boolean) =>
      `market-finder/contacts/${contactId}/${archive ? "archive" : "unarchive"}`,
    marketsDirectory: "market-finder/markets/directory",
    me: "market-finder/me",
    aimCompanies: "market-finder/aim/companies",
    aimCompanyContacts: (referenceId: number) =>
      `market-finder/aim/companies/${referenceId}/contacts`,
    marketAimImport: "market-finder/markets/aim-import",
    marketContactsAimImport: (carrierId: string) =>
      `market-finder/markets/${carrierId}/contacts/aim-import`,
  },

  /**
   * Carrier endpoints (v1)
   */
  carriers: {
    list: "carriers",
    checkEligibility: (applicationId: number) =>
      `carriers/check-eligibility/${applicationId}`,
    rateIndication: (carrierName: string) =>
      `carriers/${carrierName}/rate_indication`,
    quote: (carrierName: string) => `carriers/${carrierName}/quote`,
  },

  /**
   * Carrier issue endpoints (v1) — admin-only, multi-rater backend
   */
  carrierIssues: {
    unclassified: "carrier-issues/unclassified",
    groups: "carrier-issues/groups",
    group: (groupId: number) => `carrier-issues/groups/${groupId}`,
    assignToGroup: (groupId: number) =>
      `carrier-issues/groups/${groupId}/assign`,
    unassign: "carrier-issues/unassign",
    detail: (quoteResultId: number) =>
      `carrier-issues/quote-results/${quoteResultId}`,
    attempts: (quoteResultId: number) =>
      `carrier-issues/quote-results/${quoteResultId}/attempts`,
    recurrence: (quoteResultId: number) =>
      `carrier-issues/quote-results/${quoteResultId}/recurrence`,
    /**
     * Re-executes this row's archived carrier call. multi-rater rather than support-api
     * on purpose: only multi-rater can mint a fresh token for the carriers whose
     * archived `curl.sh` holds no auth call (Dellwood, Lio), which support-api replays
     * as a 401 and reports as a regression. Nothing is written — this is not a re-rate.
     */
    replay: (quoteResultId: number) =>
      `carrier-issues/quote-results/${quoteResultId}/replay`,
  },

  /**
   * Support-agent verdicts for carrier errors — support-api backend, NOT
   * multi-rater. The `/api/v1/` prefix is added by `lib/http/support-client.ts`,
   * so it is absent here, exactly as it is for every other support-api path.
   *
   * IMPORTANT: the error fingerprint travels as a QUERY PARAM, never as a path
   * segment. multi-rater fingerprints are opaque strings that may contain `/`,
   * which a Starlette path segment cannot round-trip — a literal and a
   * percent-encoded slash both 404 before the route dispatches. The support-api
   * routes are declared that way on purpose; see its
   * `api/v1/carrier_errors/routes.py` module docstring.
   */
  supportCarrierErrors: {
    verdict: (carrier: string) => `carrier-errors/${encodeURIComponent(carrier)}`,
    analyze: (carrier: string) =>
      `carrier-errors/${encodeURIComponent(carrier)}/analyze`,
    // Re-executes the archived carrier call for real. POST, never a GET the page
    // issues on load -- see agentVerdictRepository.replay.
    replay: (carrier: string) =>
      `carrier-errors/${encodeURIComponent(carrier)}/replay`,
  },

  /**
   * Commission endpoints (v1)
   */
  commissions: {
    schedules: "commissions",
  },

  /**
   * Knowledge layer endpoints (v1) — multi-rater backend
   */
  knowledge: {
    rateEvents: "knowledge/rate-events",
  },

  /**
   * Reports endpoints (v1)
   */
  reports: {
    quoteResults: (applicationId: number) =>
      `submissions/${applicationId}/quote_results`,
    catalog: (vertical: string) => `admin/report-config/${vertical}/catalog`,
    config: (vertical: string) => `admin/report-config/${vertical}`,
    create: (applicationId: number) =>
      `submissions/${applicationId}/reports`,
    list: "reports",
    detail: (reportId: number) => `reports/${reportId}`,
    payloads: (reportId: number) => `reports/${reportId}/payloads`,
  },

  /**
   * Underwriter endpoints (v1)
   */
  underwriters: {
    list: "underwriters",
  },
  /**
   * Property endpoints (property-risk backend)
   */
  /**
   * Bar & Tavern endpoints (bar-tavern backend)
   */
  barTavern: {
    list: "applications/",
    archivedList: "applications/archived",
    detail: (id: number) => `applications/${id}`,
    stats: "applications/stats",
    territoryOptions: "applications/territory-options",
    validateAddress: "applications/validate-address",
    previewPremium: (id: number) => `applications/${id}/preview-premium`,
    reprocess: (id: number) => `applications/${id}/reprocess`,
    uploadFile: (id: number) => `applications/${id}/upload-file`,
    verifyAccess: (id: number) => `applications/verify-access/${id}`,
    verifyCode: (id: number) => `applications/verify-code/${id}`,
    communications: (id: number) => `applications/${id}/communications`,
    /**
     * Per-location resources (BAR-238). Nested under the application because a
     * location has no meaning without its account — and because scoping the
     * lookup to the parent is what stops an id-only request reaching another
     * account's premises.
     */
    locations: (applicationId: number) => `applications/${applicationId}/locations`,
    location: (applicationId: number, locationId: number) =>
      `applications/${applicationId}/locations/${locationId}`,
    locationPrimary: (applicationId: number, locationId: number) =>
      `applications/${applicationId}/locations/${locationId}/primary`,
    locationDuplicate: (applicationId: number, locationId: number) =>
      `applications/${applicationId}/locations/${locationId}/duplicate`,
    locationPreviewPremium: (applicationId: number, locationId: number) =>
      `applications/${applicationId}/locations/${locationId}/preview-premium`,
    locationOtherCoverages: (applicationId: number, locationId: number) =>
      `applications/${applicationId}/locations/${locationId}/other-coverages`,
    locationOtherCoveragePreview: (applicationId: number, locationId: number) =>
      `applications/${applicationId}/locations/${locationId}/other-coverages/preview`,
    locationOtherCoverage: (
      applicationId: number,
      locationId: number,
      coverageId: number,
    ) =>
      `applications/${applicationId}/locations/${locationId}/other-coverages/${coverageId}`,
  },

  /**
   * Account-level Additional Coverages (BAR-240, bar-tavern backend).
   *
   * A sibling block rather than three more lines inside `barTavern` above, purely so
   * this work package does not contend for the same lines as the concurrent tracks of
   * the same epic. The `/server` prefix both paths need comes from
   * `NEXT_PUBLIC_BAR_TAVERN_API_URL`, so it is absent here — as it is above.
   */
  barTavernAdditionalCoverages: {
    update: (applicationId: number) =>
      `applications/${applicationId}/additional-coverages`,
    /** `_fee`-suffixed defaults; GET reads, PUT replaces all three. */
    adminDefaults: "admin/bar-tavern/coverage-defaults",
  },

  /**
   * AIM integration endpoints
   */
  aim: {
    quote: (applicationId: number) => `aim/${applicationId}/quote`,
    quotes: (quoteId: string) => `aim/quotes/${quoteId}`,
    searchInsured: "api/xpt/Search",
  },

  /**
   * Property endpoints (property-risk backend)
   */
  property: {
    list: "api/v1/submissions/",
    detail: (id: number) => `api/v1/submissions/${id}`,
    archive: (id: number) => `api/v1/submissions/${id}`,
    building: (appId: number, buildingId: number) =>
      `api/v1/submissions/${appId}/buildings/${buildingId}`,
    reprocess: (id: number) => `api/v1/submissions/${id}/reprocess`,
    print: (id: number) => `api/v1/submissions/${id}/print`,
    ingestPdf: "api/v1/submissions/ingest-pdf",
    prompts: "api/v1/submissions/prompts",
    stats: "api/v1/submissions/stats",
  },

  /**
   * Transportation endpoints (transportation backend)
   */
  transportation: {
    submissions: {
      list: "api/v1/transportation/submissions",
      detail: (id: number) => `api/v1/transportation/submissions/${id}`,
      counts: "api/v1/transportation/submissions/counts",
      bulkAction: "api/v1/transportation/submissions/bulk-action",
      decline: "api/v1/transportation/submissions/decline",
      evaluateDecline: (id: number) =>
        `api/v1/transportation/submissions/${id}/evaluate-decline`,
      reprocess: (id: number) =>
        `api/v1/transportation/submissions/${id}/reprocess`,
      pdf: (id: number) => `api/v1/transportation/submissions/${id}/pdf`,
      fieldByName: (id: number, fieldName: string) =>
        `api/v1/transportation/submissions/${id}/fields/by-name/${fieldName}`,
      premium: (id: number) =>
        `api/v1/transportation/submissions/${id}/premium`,
      premiumReset: (id: number) =>
        `api/v1/transportation/submissions/${id}/premium/reset`,
      premiumRecalculate: (id: number) =>
        `api/v1/transportation/submissions/${id}/premium/recalculate`,
      scheduleModHistory: (id: number) =>
        `api/v1/transportation/submissions/${id}/schedule-mod-history`,
      commodities: (id: number) =>
        `api/v1/transportation/submissions/${id}/commodities`,
      driver: (id: number, driverId: number) =>
        `api/v1/transportation/submissions/${id}/drivers/${driverId}`,
      driverAttachments: (id: number, driverId: number) =>
        `api/v1/transportation/submissions/${id}/drivers/${driverId}/attachments`,
      emails: (id: number) => `api/v1/transportation/submissions/${id}/emails`,
      attachments: (id: number) =>
        `api/v1/transportation/submissions/${id}/attachments`,
      attachmentPages: (id: number, attId: number) =>
        `api/v1/transportation/submissions/${id}/attachments/${attId}/pages`,
      attachmentArchive: (id: number, attId: number) =>
        `api/v1/transportation/submissions/${id}/attachments/${attId}/archive`,
      attachmentUnarchive: (id: number, attId: number) =>
        `api/v1/transportation/submissions/${id}/attachments/${attId}/unarchive`,
      attachmentsUpload: (id: number) =>
        `api/v1/transportation/submissions/${id}/attachments/upload`,
      taskStatus: (id: number, taskId: string) =>
        `api/v1/transportation/submissions/${id}/tasks/${taskId}/status`,
      uploadTaskCancel: (id: number, taskId: string) =>
        `api/v1/transportation/submissions/${id}/attachments/upload/${taskId}`,
      cabRefresh: (id: number) =>
        `api/v1/transportation/submissions/${id}/cab/refresh`,
      cabConfirm: (id: number) =>
        `api/v1/transportation/submissions/${id}/cab/confirm`,
      visionRetry: (id: number) =>
        `api/v1/transportation/submissions/${id}/vision/retry`,
    },
  },

  /**
   * Vertical Maker endpoints (vertical-maker service, reached via the
   * same-origin BFF proxy at /api/vertical-maker — see
   * `lib/http/vertical-maker-client.ts`). Paths are the upstream service
   * paths; the service mounts them at the root (not under /api/v1).
   */
  verticalMaker: {
    // Step 1 — identity + naming contract + uniqueness. Also re-run after
    // appending accepted document proposals to surface per-field 422 errors.
    validate: "api/validate",
    // Step 3 — enqueue the ingestion dry-run on the Celery worker; returns a
    // task handle ({ task_id, status }) that is polled via `taskStatus`.
    ingest: "api/ingest/async",
    // Step 3b — reverse classifier (proposals for uncovered documents). Costs
    // LLM calls, so it is invoked explicitly. Sync convenience path; the async
    // path enqueues on the worker and is polled via `taskStatus`.
    discover: "api/discover",
    discoverAsync: "api/discover/async",
    // Step 3 — poll the async ingestion/discovery task until it reaches a
    // terminal state (shared Celery-generic task endpoint).
    taskStatus: (taskId: string) => `api/tasks/${taskId}`,
    // Step 3b — KNOWN document catalog (name/label/kind) for the picker/badges
    // (GIQS-110, optional at runtime — callers degrade gracefully if absent).
    catalog: "api/catalog",
    // Step 2 — upload .eml/.msg sample emails; returns a server-side handle.
    emailsUpload: "api/emails/upload",
    // Step 2 — create direct PUT targets for sample emails.
    emailsUploadPresign: "api/emails/upload/presign",
    // Step 6 — full dry run + delivery plan (zero side effects).
    preview: "api/preview",
    // Persistent agent chat (generation-time forcing questions + directions).
    chat: "api/chat",
    // Step 7 — gated implementer orchestrator (real delivery; durable run state).
    implement: "api/implement",
    implementRun: (runId: string) => `api/implement/${runId}`,
    implementRetryStep: (runId: string, key: string) =>
      `api/implement/${runId}/steps/${key}/retry`,
    implementCancel: (runId: string) => `api/implement/${runId}/cancel`,
    implementResume: (runId: string) => `api/implement/${runId}/resume`,
    implementRollback: (runId: string) => `api/implement/${runId}/rollback`,
    health: "health",
  },
  /**
   * Property Insurance endpoints (property-insurance backend)
   */
  propertyInsurance: {
    gridConfig: "api/v1/property_insurance/grid-config",
    submissions: {
      list: "api/v1/property_insurance/submissions",
      detail: (id: number) => `api/v1/property_insurance/submissions/${id}`,
      buildingUpdate: (id: number, buildingId: number) =>
        `api/v1/property_insurance/submissions/${id}/buildings/${buildingId}`,
      counts: "api/v1/property_insurance/submissions/counts",
      bulkAction: "api/v1/property_insurance/submissions/bulk-action",
      decline: "api/v1/property_insurance/submissions/decline",
      evaluateDecline: (id: number) =>
        `api/v1/property_insurance/submissions/${id}/evaluate-decline`,
      reprocess: (id: number) =>
        `api/v1/property_insurance/submissions/${id}/reprocess`,
      pdf: (id: number) => `api/v1/property_insurance/submissions/${id}/pdf`,
      attachments: (id: number) =>
        `api/v1/property_insurance/submissions/${id}/attachments`,
      attachmentPages: (id: number, attId: number) =>
        `api/v1/property_insurance/submissions/${id}/attachments/${attId}/pages`,
      attachmentArchive: (id: number, attId: number) =>
        `api/v1/property_insurance/submissions/${id}/attachments/${attId}/archive`,
      attachmentUnarchive: (id: number, attId: number) =>
        `api/v1/property_insurance/submissions/${id}/attachments/${attId}/unarchive`,
      attachmentsUpload: (id: number) =>
        `api/v1/property_insurance/submissions/${id}/attachments/upload`,
      taskStatus: (id: number, taskId: string) =>
        `api/v1/property_insurance/submissions/${id}/tasks/${taskId}/status`,
      uploadTaskCancel: (id: number, taskId: string) =>
        `api/v1/property_insurance/submissions/${id}/attachments/upload/${taskId}`,
    },
  },
} as const;
